import { Navigate } from "react-router-dom";
import { resolveClipSourceStatus } from "@/lib/clips/sourceReadiness";
import Layout from "@/components/Layout";
import { LoadingCard, LoadingCardFrame } from "@/components/LoadingCard";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import ProjectEditor from "@/components/ProjectEditor";
import { useQuery } from "@apollo/client";
import { GET_MY_PROJECT, GET_MY_DATA } from "@/queries/queries";
import { getProjectStatusLabel } from "@/constants/project-status";
import type { MyProjectQuery, MyProjectQueryVariables } from "@/generated/clip-source-pages";
import { useRenderProjectClipV2Mutation, useUpdateMyProjectMutation } from "@/generated/graphql";
import { useProjectPolling } from "@/hooks/use-polling";
import { etaSecondsToMinutes } from "@/lib/format/etaMinutes";
import { useCallback } from "react";
import type { PersistedPostLayoutBlob } from "@/lib/layouts/persistPostLayout";
import { buildUpdateMyProjectOptimistic } from "@/lib/projects/buildUpdateMyProjectOptimistic";
import { FailedProjectNotice } from "@/components/projects/FailedProjectNotice";

const ProjectCopilotPage = ({ variant = 'editor' }: { variant?: 'editor' | 'studio' }) => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const initialClipId = searchParams.get('clip') || undefined;

  const { data, loading, error, refetch } = useQuery<MyProjectQuery, MyProjectQueryVariables>(GET_MY_PROJECT, {
    variables: { id: projectId as string },
    skip: !projectId,
  });

  const [renderProjectClipV2Mutation] = useRenderProjectClipV2Mutation({ refetchQueries: [{ query: GET_MY_DATA }] });

  const renderV2 = useCallback(async (variables: { id: string; input: any }) => {
    return renderProjectClipV2Mutation({ variables });
  }, [renderProjectClipV2Mutation]);

  const [updateMyProjectMutation] = useUpdateMyProjectMutation();

  const initialProject = data?.myProject;
  const status = initialProject?.curation_status;
  const isProcessing = !!status && !['success', 'failure', 'expired'].includes(status);
  const { data: polledProject } = useProjectPolling(projectId, {
    enabled: isProcessing,
  });
  const project = polledProject ?? initialProject;
  const effectiveStatus = project?.curation_status || status;
  const statusLabel = getProjectStatusLabel(effectiveStatus);
  const etaSeconds = project?.eta_seconds;
  const etaMinutes = etaSecondsToMinutes(etaSeconds);

  const handleSpeakerLayoutChange = useCallback(
    (layout: number) => {
      if (!projectId) return;
      updateMyProjectMutation({
        variables: { id: projectId, input: { speaker_layout: layout } },
        optimisticResponse: buildUpdateMyProjectOptimistic(
          { id: projectId, ...project },
          { speaker_layout: layout },
        ),
      }).catch((err) => {
        console.error("Failed to persist speaker_layout", err);
      });
    },
    [projectId, project, updateMyProjectMutation],
  );

  const handlePostLayoutChange = useCallback(
    (post_layout: PersistedPostLayoutBlob | null) => {
      if (!projectId) return;
      updateMyProjectMutation({
        variables: { id: projectId, input: { post_layout } },
        optimisticResponse: buildUpdateMyProjectOptimistic(
          { id: projectId, ...project },
          { post_layout },
        ),
      }).catch((err) => {
        console.error("Failed to persist post_layout", err);
      });
    },
    [projectId, project, updateMyProjectMutation],
  );

  if (loading) {
    return (
      <Layout>
        <LoadingCardFrame>
          <LoadingCard message={t('projectEditor.loadingProject')} />
        </LoadingCardFrame>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-2">
            <p className="text-sm text-destructive">{t('projectEditor.errorLoadingProject')}</p>
            <p className="text-xs text-muted-foreground">{error.message}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-sm text-muted-foreground">{t('projectEditor.projectNotFound')}</p>
        </div>
      </Layout>
    );
  }

  if (project && resolveClipSourceStatus(project.source_status) !== 'ready'
      && project.curation_status === 'success') {
    return <Navigate to={`/project/${projectId}/clips`} replace />;
  }

  if (effectiveStatus === 'failure') {
    return (
      <Layout>
        <FailedProjectNotice
          kind="project"
          id={projectId}
          failureCode={project?.failure_code}
          videoUrl={project?.compressed_base_video_url}
          durationSeconds={project?.duration_seconds}
          rangeStart={project?.start_time}
          onConverted={() => refetch()}
        />
      </Layout>
    );
  }

  if (effectiveStatus === 'expired') {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-sm text-muted-foreground">{t('projectEditor.projectExpired')}</p>
        </div>
      </Layout>
    );
  }

  if (effectiveStatus && effectiveStatus !== 'success') {
    return (
      <Layout>
        <LoadingCardFrame>
          <LoadingCard
            message={statusLabel}
            status={effectiveStatus}
            etaLabel={etaMinutes != null
              ? t('projectEditor.estimatedTime', { minutes: etaMinutes })
              : undefined}
          />
        </LoadingCardFrame>
      </Layout>
    );
  }

  return (
    <Layout>
      <ProjectEditor
        variant={variant}
        project={project}
        initialClipId={initialClipId}
        renderV2={renderV2}
        onSpeakerLayoutChange={handleSpeakerLayoutChange}
        onPostLayoutChange={handlePostLayoutChange}
      />
    </Layout>
  );
};

export default ProjectCopilotPage;
